"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { adsense_client_id } from "@/app/constants";

const ADSENSE_SRC = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adsense_client_id}`;

// Page groups that are never ad-eligible regardless of account, matching the
// actual folder-based routes (capitalized, no route-group segments in the URL).
const EXCLUDED_PATH_PREFIXES = ["/Comics", "/Games"];

const isExcludedPath = (pathname: string | null) =>
  Boolean(pathname) &&
  EXCLUDED_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname!.startsWith(`${prefix}/`));

export const AdSenseLoader = () => {
  const { data: session } = useSession();
  const pathname = usePathname();

  // Fail-safe default: an account is ad-gated the moment it's confirmed to
  // be used for a child (see /api/signup/child-profile), since there's no
  // profile-switching and the parent's login is the same session the child
  // uses. Never add the AdSense script itself for such a session.
  const isMinor = session?.user?.isMinor === true;
  const blocked = isMinor || isExcludedPath(pathname);

  useEffect(() => {
    if (blocked) return;
    if (document.querySelector(`script[src="${ADSENSE_SRC}"]`)) return;

    // A plain <script> appended to <head>, deliberately not next/script:
    // next/script adds a data-nscript attribute and places the tag in <body>,
    // and AdSense logs "AdSense head tag doesn't support data-nscript
    // attribute". With that tag, Google's consent message (googlefc) never
    // initialized on this site.
    const script = document.createElement("script");
    script.async = true;
    script.src = ADSENSE_SRC;
    script.crossOrigin = "anonymous";
    document.head.appendChild(script);
  }, [blocked]);

  return null;
};
